import { useStateMachine } from 'little-state-machine';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FormGroup } from '../../../../Components/FormGroup/FormGroup';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../_helpers/_methods';
import NavigationButtonGroup from '../../../admin/Events/Steps/components/NavigationButtonGroup';
import PhysicalLocationDetails from '../../../admin/Events/Steps/components/PhysicalLocationDetails';
import updateAction from '../updateAction';

function TraineeStep1() {
  const { actions } = useStateMachine({ updateAction });
  const { state } = useStateMachine({ updateAction });
  const navigate = useNavigate();
  const { id, edit: isEdit } = useParams();
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {}
  });
  const [isLoading, setIsLoading] = useState(false);
  const trainee = useSelector((state) => state.trainees.trainees);
  const location = useLocation();
  const [employeeTypeID, setEmployeeTypeID] = useState();

  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [employeeStructure, setEmployeeStructure] = useState(null);
  const [employeeDepartment, setEmployeeDepartment] = useState(null);

  useEffect(() => {
    const getEmployeeTypeData = async () => {
      try {
        let response = await fetchWrapper.get('/employee-type/');
        setEmployeeTypes(response.data.results);
      } catch (error) {
        ErrorMessageHandler(error);
      }
    };
    getEmployeeTypeData();
  }, []);

  useEffect(() => {
    if (isEdit) {
      setValue('office_name', trainee.office_name);
      setValue('trainee_department', trainee.department);
      setValue('trainee_post', trainee.post);

      if (Array.isArray(trainee?.employee_type)) {
        if (employeeTypes.length) {
          const employeeKind = trainee?.employee_type[0];
          handleSettingKindData(employeeKind?.id, employeeKind?.title);

          if (trainee.employee_type.length > 0) setEmployeeTypeID(trainee?.employee_type.at(-1).id);
        }
      }
    }
  }, [trainee, employeeTypes]);

  useEffect(() => {
    if (isEdit && employeeStructure) {
      const tempEmployeeStructure = trainee?.employee_type[1];
      const employeeDepartment = trainee?.employee_type[2];
      handleSettingStructureData(tempEmployeeStructure?.id, tempEmployeeStructure?.title);
      setValue('trainee_office[department]', employeeDepartment?.title);
    }
  }, [employeeStructure]);

  let options = useMemo(() => {
    return employeeTypes?.map((item) => ({
      label: item.title,
      value: item.title,
      id: `trainee_kind_${item.title}_${item.id}`,
      name: 'trainee_office[kind]'
    }));
  }, [employeeTypes]);

  const handleTypeChange = (e) => {
    let employeeKindId = e.target.id.split('_')[e.target.id.split('_').length - 1];
    handleSettingKindData(employeeKindId, e.target.value);
    setEmployeeDepartment(null);
  };

  const handleSettingKindData = (employeeKindId, employeeKindValue) => {
    let tempEmployeeTypeKind = employeeTypes.find((kind) => kind.id === Number(employeeKindId));
    if (tempEmployeeTypeKind) {
      let tempEmployeeStructure = tempEmployeeTypeKind.childrens;
      if (tempEmployeeStructure) {
        tempEmployeeStructure = tempEmployeeStructure.map((item) => ({
          label: item.title,
          value: item.title,
          id: `trainee_structure_${item.title}_${item.id}`,
          name: 'trainee_office[structure]',
          ...item
        }));
        if (tempEmployeeStructure.length) {
          setEmployeeStructure(tempEmployeeStructure);
        } else {
          setEmployeeStructure(null);
        }
      }
      setEmployeeTypeID(employeeKindId);
      setValue('trainee_office[kind]', employeeKindValue);
      setValue('trainee_office[structure]', null);
      setValue('trainee_office[department]', null);
    }
  };
  const handleStructureChange = (e) => {
    let employeeStructureId = e.target.id.split('_')[e.target.id.split('_').length - 1];
    handleSettingStructureData(employeeStructureId, e.target.value);
  };

  const handleSettingStructureData = (employeeStructureId, employeeStructureValue) => {
    let tempEmployeeStructure = employeeStructure.find(
      (struc) => struc.id === Number(employeeStructureId)
    );
    if (tempEmployeeStructure) {
      let tempEmployeeDepartment = tempEmployeeStructure.children;
      if (tempEmployeeDepartment?.length) {
        tempEmployeeDepartment = tempEmployeeDepartment.map((item) => ({
          label: item.title,
          value: item.title,
          id: `trainee_department_${item.title}_${item.id}`,
          name: 'trainee_office[department]',
          ...item
        }));

        if (tempEmployeeDepartment.length) {
          setEmployeeDepartment(tempEmployeeDepartment);
        } else {
          setEmployeeDepartment(null);
        }
      }
      setEmployeeTypeID(employeeStructureId);
      setValue('trainee_office[structure]', employeeStructureValue);
      setValue('trainee_office[department]', null);
    }
  };

  const handleDepartmentChange = (e) => {
    setEmployeeTypeID(e.target.id.split('_')[e.target.id.split('_').length - 1]);
    setValue('trainee_office[department]', e.target.value);
  };
  const onSubmit = (data) => {
    setIsLoading(true);
    data = { ...data, employee_type: employeeTypeID };
    actions.updateAction(data);

    let path = location.pathname.split('/');
    setIsLoading(false);
    if (isEdit) {
      navigate(`/admin/trainees/${id}/edit/2`);
    } else {
      path.includes('admin') ? navigate('/admin/users/trainees/2') : navigate('/trainees/apply/2');
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="step-1 py-3">
          <div className="row">
            <h3 className="my-2">
              <FormattedMessage id="participant_details" />
            </h3>
            <div className="col-md-3">
              <StyledTraineeKindWrapper className="ps-2 py-2 border-dashed">
                <FormGroup
                  id="trainee_office[kind]"
                  type="radio"
                  register={register}
                  onChange={(e) => handleTypeChange(e)}
                  options={options}
                  defaultValue={
                    state?.trainee_office?.kind ||
                    (isEdit && trainee.employee_type && trainee.employee_type[0]?.title)
                  }
                  error={{
                    [`trainee_office[kind]`]: errors?.trainee_office && errors?.trainee_office.kind
                  }}
                  required={true}
                  errorId={'trainee_office_kind'}
                />
              </StyledTraineeKindWrapper>
            </div>

            {employeeStructure && (
              <div className="col-md-3 ps-0">
                <StyledTraineeKindWrapper className="ps-2 py-2 border-dashed">
                  <FormGroup
                    id="trainee_office[structure]"
                    type="radio"
                    onChange={(e) => handleStructureChange(e)}
                    defaultValue={
                      state?.trainee_office?.structure ||
                      (isEdit && trainee.employee_type && trainee.employee_type[1]?.title)
                    }
                    options={employeeStructure}
                    register={register}
                    // error={errors}
                    error={{
                      [`trainee_office[structure]`]:
                        errors?.trainee_office && errors?.trainee_office.structure
                    }}
                    errorId={'trainee_office_structure'}
                    required={true}
                  />
                </StyledTraineeKindWrapper>
              </div>
            )}

            {employeeDepartment && (
              <div className="col-md-3 ps-0">
                <StyledTraineeKindWrapper className="ps-2 py-2 border-dashed">
                  <FormGroup
                    id="trainee_office[department]"
                    type="radio"
                    onChange={(e) => handleDepartmentChange(e)}
                    defaultValue={
                      state?.trainee_office?.department ||
                      (isEdit && trainee.employee_type && trainee.employee_type[2]?.title)
                    }
                    options={employeeDepartment}
                    register={register}
                    error={{
                      [`trainee_office[department]`]:
                        errors?.trainee_office && errors?.trainee_office.department
                    }}
                    errorId={'trainee_office_department'}
                    required={true}
                  />
                </StyledTraineeKindWrapper>
              </div>
            )}

            <h3 className="my-2">
              <FormattedMessage id="office_detail" />
            </h3>
            <div className="col-md-6 mb-3">
              <FormattedMessage id="office_name">
                {(placeholder) => (
                  <FormGroup
                    id="office_name"
                    defaultValue={state?.office_name}
                    type="text"
                    placeholder={placeholder[0]}
                    required={true}
                    register={register}
                    error={errors}
                    label={<FormattedMessage id="office_name" />}
                    errorId={`office_name`}
                  />
                )}
              </FormattedMessage>
            </div>
            <div className="col-md-3">
              <FormattedMessage id="department_placeholder">
                {(placeholder) => (
                  <FormGroup
                    id="trainee_department"
                    defaultValue={state?.trainee_department}
                    placeholder={placeholder[0]}
                    type="text"
                    register={register}
                    error={errors}
                    label={<FormattedMessage id="department" />}
                  />
                )}
              </FormattedMessage>
            </div>
            <div className="col-md-3">
              <FormattedMessage id="position_placeholder">
                {(placeholder) => (
                  <FormGroup
                    id="trainee_post"
                    defaultValue={state?.trainee_post}
                    type="text"
                    placeholder={placeholder[0]}
                    register={register}
                    error={errors}
                    label={<FormattedMessage id="position" />}
                  />
                )}
              </FormattedMessage>
            </div>
            <div className="col-md-12">
              <PhysicalLocationDetails
                type="trainees_office_address"
                isEdit={isEdit}
                id="trainees_office_address"
                register={register}
                errors={errors}
                clearErrors={clearErrors}
                setValue={setValue}
              />
            </div>
          </div>
        </div>
        <div className="registration-footer">
          <hr />
          <NavigationButtonGroup
            isLoading={isLoading}
            backTo={
              isEdit
                ? '/admin/users/trainees/list'
                : location.pathname.split('/').includes('admin')
                ? '/admin/users/trainees/list'
                : ''
            }
            // showSubmit={id}
          />
        </div>
      </form>
    </>
  );
}

export default TraineeStep1;

const StyledTraineeKindWrapper = styled.div`
  .styledformgroup {
    margin-bottom: 0 !important;
    .form-check {
      width: 100%;
    }
  }
`;
